import { z } from "zod";

import {
  filterSchema,
  type Filter,
  type FilterMatchMode,
} from "@/shared/domain/query/filters";

/**
 * Filter controls as persisted — one wire shape shared by saved Views
 * (`ViewDefinitionV1.filters`) and saved dashboards
 * (`dashboard_definition.filters`), mirroring the backend's
 * `roboto.query.SavedFilters`: snake_case `match_mode`, unrecognised keys
 * rejected (`extra="forbid"` there). UI state keeps `matchMode` and bare metric
 * paths, so the conversions below are the only place the two shapes meet.
 */
export const savedFiltersSchema = z.strictObject({
  filters: z.array(filterSchema),
  match_mode: z.enum(["AND", "OR"]).default("AND"),
});

export type SavedFilters = z.infer<typeof savedFiltersSchema>;

/**
 * Mirrors `METRIC_FIELD_PREFIX` in `roboto.query.filters`; the backend rejects
 * a bare metric path at parse time.
 */
const METRIC_FIELD_PREFIX = "metric.";

/**
 * Prefix a metric filter's field on the way out. The UI holds the bare path
 * (`navigation.path_deviation`) because that is what a chip shows; the stored
 * form carries the prefix so `field` means the same thing as in a `Condition`,
 * and a later migration to a query copies it across rather than special-casing
 * metrics.
 *
 * `filterToCondition` prefixes separately from bare UI state, so the query path
 * is unaffected.
 */
function toWireFilter(filter: Filter): Filter {
  return filter.type === "metric"
    ? { ...filter, field: `${METRIC_FIELD_PREFIX}${filter.field}` }
    : filter;
}

function fromWireFilter(filter: Filter): Filter {
  return filter.type === "metric" &&
    filter.field.startsWith(METRIC_FIELD_PREFIX)
    ? { ...filter, field: filter.field.slice(METRIC_FIELD_PREFIX.length) }
    : filter;
}

export function toSavedFilters(
  filters: Filter[],
  matchMode: FilterMatchMode,
): SavedFilters {
  return {
    filters: filters.map(toWireFilter),
    match_mode: matchMode,
  };
}

export function fromSavedFilters(saved: SavedFilters): {
  filters: Filter[];
  matchMode: FilterMatchMode;
} {
  return {
    filters: saved.filters.map(fromWireFilter),
    matchMode: saved.match_mode,
  };
}
